import React, { memo, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';

import { MoreOutlined as MoreVertIcon, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Dropdown, Table, Tooltip } from 'antd';

import Alert from 'components/Alert';
import Truncate from 'components/Truncate';
import { formatDate } from 'helpers/date';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import TableOptions from 'v2/components/Shared/TableOptions';
import type { IAffiliate } from 'v2/interfaces/affiliate';
import affiliateService from 'v2/services/affiliate';

import CustomCommissionForm from './CustomCommissionForm';
import ModalNewMessage from './ModalNewMessage';

interface IProps {
  onSort: (sortField: string, sortDirection: 'asc' | 'desc') => void;
}

const TableApproved = ({ onSort }: IProps) => {
  const history = useHistory();

  const [isLoading] = useObservable(() => affiliateService.isLoading(), []);
  const [affiliates] = useObservable(() => affiliateService.getAffiliates(), []);
  const [pagination] = useObservable(() => affiliateService.getPaginationResponse(), []);
  const [currentSort] = useObservable(() => affiliateService.getCurrentSort(), []);

  const [affiliateSelected, setAffiliateSelected] = useState<IAffiliate | null>(null);
  const [affiliatesSelected, setAffiliatesSelected] = useState<Array<IAffiliate>>([]);
  const [modalNewMessage, setModalNewMessage] = useState(false);
  const [openModalCommision, setOpenModalCommision] = useState(false);

  const openModalCommission = (affiliate: IAffiliate) => {
    setAffiliateSelected(affiliate);
    setOpenModalCommision(true);
  };

  const closeModalCommission = () => {
    setOpenModalCommision(false);
    setAffiliateSelected(null);
  };

  const toggleModalNewMessage = () => {
    setModalNewMessage(!modalNewMessage);
  };

  const onChangeRowsPerPage = (perPage: number) => {
    affiliateService.setPagination({ ...pagination, ...{ per_page: perPage, current_page: 1 } });
    document.getElementById('affiliates_table')?.scrollIntoView();
  };

  const onChangePage = (page: number) => {
    affiliateService.setPagination({ ...pagination, ...{ current_page: page } });
    document.getElementById('affiliates_table')?.scrollIntoView();
  };

  const resetAffiliatesSelected = () => {
    setAffiliatesSelected([]);
  };

  const selectAllAffiliates = () => {
    const selected = affiliates.map(a => a);

    if (affiliates.length > affiliatesSelected.length) {
      setAffiliatesSelected(selected);
      return;
    }

    setAffiliatesSelected([]);
  };

  const toggleSelect = (affiliate: IAffiliate) => {
    if (affiliatesSelected.filter(a => a.id === affiliate.id).length) {
      setAffiliatesSelected(affiliatesSelected.filter(a => a.id !== affiliate.id));
      return;
    }

    setAffiliatesSelected([...affiliatesSelected, affiliate]);
  };

  const handleClickModerate = (id: number, status: 'A' | 'R') => {
    affiliateService.moderateAffiliate(id, status).subscribe(() => {
      setAffiliatesSelected([]);
      toast.success('Afiliação cancelada com sucesso.');
    }, toast.error);
  };

  const repprovedAffiliates = async () => {
    const confirmation = await Alert.show({
      title: 'Atenção',
      message: 'Deseja realmente cancelar a afiliação dos afiliados selecionados?',
      confirmation: true,
      buttonOk: 'Cancelar afiliação',
      buttonCancel: 'Cancelar'
    });
    if (!confirmation) return;

    const affs = affiliatesSelected.map(a => a.id);
    affiliateService.moderateMultipleAffiliates(affs, 'R').subscribe(() => {
      setAffiliatesSelected([]);
      toast.success('Afiliados reprovados com sucesso.');
    }, toast.error);
  };

  const checkIfAffiliateIsSelect = (affiliateId: number) =>
    !!affiliatesSelected.filter(a => a.id === affiliateId).length;

  const goToMessagesPage = (affiliate: IAffiliate) => {
    history.push(`/producer/messages?affiliate=${affiliate.affiliate_id}`);
  };

  const handleSortClick = (field: string) => {
    const isAsc = currentSort?.field === field && currentSort?.direction === 'asc';
    onSort(field, isAsc ? 'desc' : 'asc');
  };

  const columns: any[] = [
    {
      title: () => (
        <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
          <Checkbox
            indeterminate={!!affiliatesSelected?.length && affiliatesSelected?.length < affiliates?.length}
            checked={!!affiliates?.length && affiliatesSelected?.length === affiliates?.length}
            onChange={() => selectAllAffiliates()}
            disabled={!affiliates?.length}
          />
        </PermissionHide>
      ),
      width: 45,
      render: (_: any, affiliate: IAffiliate) => (
        <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
          <Checkbox checked={checkIfAffiliateIsSelect(affiliate.id)} onClick={() => toggleSelect(affiliate)} />
        </PermissionHide>
      )
    },
    {
      title: 'Data da solicitação',
      width: 170,
      sorter: true,
      sortOrder:
        currentSort?.field === 'request_date' ? (currentSort?.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (_: any, affiliate: IAffiliate) => <span>{formatDate(affiliate.request_date)}</span>,
      onHeaderCell: () => ({ onClick: () => handleSortClick('request_date') })
    },
    {
      title: 'Produto',
      width: 250,
      sorter: true,
      sortOrder:
        currentSort?.field === 'product_title' ? (currentSort?.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (_: any, affiliate: IAffiliate) => (
        <>
          <span className='text-xs font-semibold'>
            <Truncate count={30} final='...'>
              {affiliate.product_title}
            </Truncate>
          </span>
          <span className='text-xs'>({affiliate.product_id})</span>
        </>
      ),
      onHeaderCell: () => ({ onClick: () => handleSortClick('product_title') })
    },
    {
      title: 'Afiliado',
      sorter: true,
      sortOrder:
        currentSort?.field === 'affiliate_name' ? (currentSort?.direction === 'asc' ? 'ascend' : 'descend') : null,
      render: (_: any, affiliate: IAffiliate) => (
        <span>
          <a href={`/producer/detail/${affiliate.affiliate_id}`} target='_blank' rel='noopener noreferrer'>
            <Truncate count={25} final={'...'}>
              {affiliate.affiliate_name}
            </Truncate>
          </a>
          {affiliate.affiliate_email}
        </span>
      ),
      onHeaderCell: () => ({ onClick: () => handleSortClick('affiliate_name') })
    },
    {
      title: 'F.I',
      width: 50,
      render: (_: any, affiliate: IAffiliate) => <span>{affiliate.funnel_leads}</span>
    },
    {
      title: 'Vendas',
      width: 90,
      render: () => (
        <Tooltip title='Em breve voltaremos a mostrar esses valores'>
          <div className='flex cursor-pointer'>
            Indisponível{' '}
            <span className='m-1 text-sm! font-normal text-[#616161]'>
              <QuestionCircleOutlined />
            </span>
          </div>
        </Tooltip>
      )
    },
    {
      title: 'Faturamento',
      width: 130,
      render: () => (
        <Tooltip title='Em breve voltaremos a mostrar esses valores'>
          <div className='flex cursor-pointer'>
            Indisponível{' '}
            <span className='m-1 text-sm! font-normal text-[#616161]'>
              <QuestionCircleOutlined />
            </span>
          </div>
        </Tooltip>
      )
    },
    {
      title: 'Com. diferenciada',
      width: 190,
      render: (_: any, affiliate: IAffiliate) => (
        <span>
          {affiliate.custom_commission === 0 && affiliate.custom_commission_type === null ? (
            'Não configurado'
          ) : (
            <>
              {affiliate.custom_commission_type === 'V' && (
                <FormattedNumber value={affiliate.custom_commission} format='BRL' />
              )}
              {affiliate.custom_commission_type === 'P' && (
                <>
                  <FormattedNumber value={affiliate.custom_commission} />%
                </>
              )}
            </>
          )}
        </span>
      )
    },
    {
      title: '',
      width: 50,
      fixed: 'right',
      render: (_: any, affiliate: IAffiliate) => (
        <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
          <Dropdown
            menu={{
              items: [
                { key: 'commission', label: 'Comissão diferenciada', onClick: () => openModalCommission(affiliate) },
                { key: 'message', label: 'Enviar mensagem', onClick: () => goToMessagesPage(affiliate) },
                { key: 'cancel', label: 'Cancelar afiliação', onClick: () => handleClickModerate(affiliate.id, 'R') }
              ]
            }}
            trigger={['click']}
          >
            <Button type='text' icon={<MoreVertIcon />} />
          </Dropdown>
        </PermissionHide>
      )
    }
  ];

  return (
    <>
      {!!affiliatesSelected.length && (
        <div className='relative mb-[70px] max-md:mb-[100px]'>
          <TableOptions
            items={affiliatesSelected.length}
            style={{ top: 0, padding: 16 }}
            options={[
              { label: 'Cancelar', callback: () => resetAffiliatesSelected() },
              { icon: 'thumb_down', label: 'Cancelar Afiliação', callback: () => repprovedAffiliates() },
              { icon: 'message', label: 'Enviar Mensagem', color: 'primary', callback: () => toggleModalNewMessage() }
            ]}
          />
        </div>
      )}

      <div>
        <Table
          id='affiliates_table'
          scroll={{ x: 'max-content' }}
          columns={columns}
          loading={isLoading}
          dataSource={affiliates || []}
          rowKey='id'
          pagination={
            pagination
              ? {
                  current: pagination?.current_page || 1,
                  pageSize: pagination?.per_page || 10,
                  total: pagination?.total_items || 0,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100', '200'],
                  onChange: page => onChangePage(page),
                  onShowSizeChange: (_, size) => onChangeRowsPerPage(size)
                }
              : false
          }
          sortDirections={['ascend', 'descend']}
          showSorterTooltip={false}
        />
      </div>

      <ModalNewMessage
        active={modalNewMessage}
        closeModal={toggleModalNewMessage}
        affiliatesSelected={affiliatesSelected}
      />
      <CustomCommissionForm
        open={openModalCommision}
        handleClose={closeModalCommission}
        affiliate={affiliateSelected}
      />
    </>
  );
};

export default memo(TableApproved);
